import { v4 as uuidV4 } from 'uuid'
import { BEGIN, REVERT } from 'redux-optimist'

import graphql from 'api/graphql'
import { bulkChangesetDefinition, singleChangesetDefinition } from 'api/tickets'

import * as types from 'constants/action_types'

import { doAutoRedirect, doOpenCurrentSearch } from 'actions/autoRedirect'
import {
  doClearNonNamedTicketSearchResults,
  doLoadIncompleteSearch,
} from 'actions/search'

import {
  oauthTokenSelector,
  selectCurrentTicketListRouteAction,
} from 'selectors/app'
import { selectIsMailboxInaccessible } from 'selectors/mailboxes'
import {
  selectPrefersClassicView,
  selectPrefersOpenFolderOnTicketSave,
  selectCurrentUserPrefersAutoadvanceToNextTicket,
} from 'ducks/currentUser/selectors'
import { selectCurrentTicketId } from 'selectors/tickets'
import { selectNextTicketIdInList } from 'selectors/ticket_list'
import { selectCurrentTicketSearchQueryId } from 'selectors/search'

import debug from 'util/debug'
import { ticketHasAttachments } from 'util/draft'
import { runOnNextTick } from 'util/functions'
import { pick } from 'util/objects'
import { batchActions } from 'util/redux'
import { hash, reverseHashInt } from 'util/scatterSwap'
import { getRawId } from 'util/globalId'
import { normalizeDiffSearchesPayload } from 'util/search'

import ChangesetQueue from './changesetQueue'

const getRecipientInput = user =>
  pick(['id', 'email', 'phone_number', 'name'], user)

const buildSingleChangesetInput = (
  ticketId,
  data,
  state,
  userInitiated,
  changesetId
) => {
  const isForwarding = data.isForwarding

  // NOTE (jscheel || marcinbunsch): To remove an assignee or group (or
  // snoozeState), you MUST pass null. Undefined will be ignored, as we
  // strict check so that undefined values don't accidentally creep in on
  // this uber- method.
  const variables = {
    ticketId,
    commentId: data.commentId,
    mailboxId: data.mailboxId,
    assignee: data.assignee ? data.assignee.id : null,
    removeAssignee: data.assignee === null,
    group: data.assigned_group_id,
    snoozeUntil: data.snoozeUntil,
    removeSnooze: data.removeSnooze,
    removeGroup: data.assigned_group_id === null,
    priority: data.priority,
    state: data.state,
    title: data.title,
    tagsToAddById: data.labels && data.labels.map(label => label.id),
    tagsToRemoveById:
      data.removeLabels && data.removeLabels.map(label => label.id),
    delete: data.delete,
    deleteMode: data.deleteMode,
    restore: data.restore,
    templateId: data.templateId,
    integration: data.integration,
    customer: data.customer,
    userInitiated,
    changesetId,
    follows: data.follows,
    deleteCommentId: data.deleteCommentId,
    updateCommentId: data.updateCommentId,
    reactionToAdd: data.reactionToAdd,
    reactionToRemove: data.reactionToRemove,
  }

  if (data.requiredState) variables.requiredState = data.requiredState
  if (data.removeDraftId) variables.removeDraftId = data.removeDraftId
  if (data.includeTicketInChangesetResponse) {
    variables.includeTicketInChangesetResponse =
      data.includeTicketInChangesetResponse
  }

  // NOTE (jscheel): Instead of having to remember a nested structure for
  // the message input object throughout the app, we simple assemble it here.

  if (isForwarding || data.body || data.note || ticketHasAttachments(data)) {
    const message = (({ body, note, isNote }) => ({ body, note, isNote }))(data)

    if (data.attachments) {
      const keys = Object.keys(data.attachments)
      message.attachments = keys.map(key => {
        const attachment = data.attachments[key]
        return {
          key: attachment.key,
          file_name: attachment.filename,
          content_type: attachment.content_type,
          file_size: attachment.file_size,
        }
      })
    }

    // perform sanitization on the message body to clean up quills
    // block level tags
    if (data.to) message.to = data.to.map(getRecipientInput)
    if (data.cc) message.cc = data.cc.map(getRecipientInput)
    if (data.bcc) message.bcc = data.bcc.map(getRecipientInput)
    if (typeof data.quotedText === 'string')
      message.quotedText = data.quotedText

    message.isForwarding = isForwarding || false
    message.isNote = message.isNote || false
    if (message.isNote) {
      message.note = message.body
      message.body = undefined
    }
    variables.message = message
  }

  return variables
}

const applyAutomaticActionsToChangeset = (variables, automaticActions) => {
  const newVariables = Object.assign({}, variables)

  automaticActions.forEach(action => {
    const { type, value } = action

    switch (type) {
      case 'status':
        newVariables.state = value
        break
      case 'labels_by_id': {
        const tagsToAdd = []
        const values = (value || '').split(',')

        values.forEach(v => {
          const tagId = reverseHashInt(getRawId(v))
          if (tagId !== null) tagsToAdd.push(tagId)
        })

        newVariables.tagsToAddById = tagsToAdd
        break
      }
      case 'label_remove': {
        const tagId = reverseHashInt(getRawId(value))
        if (tagId !== null) newVariables.tagsToRemoveById = [tagId]

        break
      }
      case 'label_remove_all':
        newVariables.removeAllLabels = true
        break
      case 'assignee_id':
        newVariables.assignee = hash(value)
        newVariables.group = null
        newVariables.removeAssignee = false
        newVariables.removeGroup = true
        break
      case 'assignee_group_id':
        newVariables.group = hash(value)
        newVariables.assignee = null
        newVariables.removeAssignee = true
        newVariables.removeGroup = false
        break
      case 'snooze_until':
        newVariables.snoozeUntil = value
        delete newVariables.removeSnooze
        break
      default:
    }
  })

  return newVariables
}

export function doSendChangeset(ticketId, data, options = {}) {
  const {
    userInitiated = true,
    optimisticData = null,
    disableNavigateToFolder = false,
    changesetId: overrideChangesetId,
    preserveDraft = false,
    mutation = singleChangesetDefinition,
    // Don't refresh the next page if true (see refreshMiddleware)
    disableRefreshPage,
  } = options

  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const changesetId = overrideChangesetId || uuidV4()
    let variables = buildSingleChangesetInput(
      ticketId,
      data,
      state,
      userInitiated,
      changesetId
    )
    const isForNewTicket = data.ticketId === 'new'

    variables.skipTicketTouch = options.skipTicketTouch

    if (data.automaticActions) {
      variables = applyAutomaticActionsToChangeset(
        variables,
        data.automaticActions
      )
    }

    const isCustomerChangeHappened = !!variables.customer
    const isIntegrationChange = !!variables.integration
    const isStarring = !!variables.priority

    // NOTE: We need to get this before the optimistic update applies any diffs
    const nextTicketId = selectNextTicketIdInList(state) || null

    // (Optimistically) navigate based on the ticket save user preference. We
    // only do this if there is no message (ie. a note or a reply) (It's
    // expected that the doSendReply/Note functions handle the on_ticket_reply
    // and on_note_add preferences).
    //
    // NOTE: The order is important! It needs to come after the (optimistic)
    // changeset request so folder/searches already have diffs applied
    //
    // (pnagy) we never want to navigate after user change action
    // (pnagy) we never want to navigate after integreation change action
    // (pnagy) we never want to navigate after starring or unstarring
    if (
      userInitiated &&
      !disableNavigateToFolder &&
      !isCustomerChangeHappened &&
      !isIntegrationChange &&
      !isStarring
    ) {
      runOnNextTick(() => {
        const classicView = selectPrefersClassicView(state)
        if (classicView) {
          const shouldOpenFolder = selectPrefersOpenFolderOnTicketSave(state)
          const routeAction = selectCurrentTicketListRouteAction(state)
          const autoAdvance = selectCurrentUserPrefersAutoadvanceToNextTicket(
            state
          )
          // NOTE: I don't know if this shouldOpenFolder thing is even a thing
          // anymore. I don't see it in the new settings pages
          if (autoAdvance && shouldOpenFolder && routeAction) {
            dispatch(routeAction)
          }
        } else if (nextTicketId) {
          dispatch(doAutoRedirect(ticketId, nextTicketId))
        }
      })
    }

    // While the actual API request may be delayed, scheduled, we want this to
    // execute immediately all the time, since it applies our optimistic updates
    dispatch({
      type: types.CREATE_CHANGESET_REQUEST,
      data: {
        ...optimisticData,
        preserveDraft,
        draft: data, // in case we need to undo a send
      },
      optimist: optimisticData ? { type: BEGIN, id: changesetId } : null,
      meta: {
        requestId: changesetId,
        isForNewTicket,
      },
    })

    const failAction = {
      type: types.CREATE_CHANGESET_FAIL,
      optimist: optimisticData ? { type: REVERT, id: changesetId } : null,
    }

    const runRequest = (id, resolve, reject) => {
      const getData = graphql(token, mutation, variables, changesetId).then(
        res => {
          const response = res.json.data
          const responseData = response.createChangeset
          responseData.diff = JSON.parse(responseData.diff) || {}
          return responseData
        }
      )
      const currentQueryId = selectCurrentTicketSearchQueryId(state)

      const successAction = getData.then(responseData => {
        return {
          type: types.CREATE_CHANGESET_SUCCESS,
          data: {
            ...responseData,
            ticketId: responseData.id,
            ticketData: responseData.ticket,
            actions: responseData.actions.records,
            diff: {
              ...responseData.diff,
              searches: {
                ...(optimisticData && optimisticData.diff
                  ? optimisticData.diff.searches
                  : null),
                ...responseData.diff.searches,
              },
            },
            options: { currentQueryId },
            preserveDraft,
            draft: data, // in case we need to undo a send
          },
          meta: {
            requestId: changesetId,
            isForNewTicket,
          },
          // Kevin Rademan: Right, I changed this to commit rather than revert.
          // Glenn originally said that this can/will cause in incorrect counts
          // but we'll have to deal with that some other way as reverting the change
          // causes other issues with sorting.
          optimist: optimisticData ? { type: REVERT, id: changesetId } : null,
        }
      })

      return (
        successAction
          .then(action => {
            const result = ChangesetQueue.handleComplete(dispatch, id, action)
            dispatch(doLoadIncompleteSearch())
            return result
          })
          .then(() => {
            if (resolve) resolve(getData)
            return getData
          })
          .catch(err => {
            debug(err)
            ChangesetQueue.handleFail(dispatch, id, failAction)
            throw err
          })
          // intentionally chain the rejections so that any exceptions in the
          // code above are properly rejected, and the queues dont end up in a
          // bad state.
          .catch(err => {
            if (reject) {
              reject(err)
            } else {
              throw err
            }
          })
      )
    }

    return ChangesetQueue.track(ticketId, runRequest, disableRefreshPage)
  }
}

export function doSendBulkChangeset(
  inputs,
  {
    userInitiated = true,
    optimisticData = null,
    defaultChangesetId = null, // recommend using the optimistic changeset ID
    snackbar,
  } = {}
) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    const changesetId = defaultChangesetId || uuidV4() // 'bulk' changeset id.

    dispatch({
      type: types.CREATE_BULK_CHANGESET_REQUEST,
      data: {
        tickets: optimisticData,
      },
      optimist: optimisticData ? { type: BEGIN, id: changesetId } : null,
    })

    const mutation = bulkChangesetDefinition

    const variables = {
      inputs: inputs.map(data =>
        buildSingleChangesetInput(
          data.ticketId,
          data,
          state,
          userInitiated,
          changesetId
        )
      ),
    }

    let movedToInaccesibleMailbox = false
    if (variables.inputs.length === 1) {
      movedToInaccesibleMailbox = selectIsMailboxInaccessible(
        state,
        variables.inputs[0].mailboxId
      )
    }

    if (movedToInaccesibleMailbox) {
      runOnNextTick(() => {
        dispatch(doOpenCurrentSearch())
      })
    }

    if (!movedToInaccesibleMailbox && userInitiated) {
      runOnNextTick(() => {
        const afterState = getState()
        const currentTicketId = selectCurrentTicketId(afterState)
        const nextTicketId = selectNextTicketIdInList(afterState) || null
        if (currentTicketId && nextTicketId) {
          dispatch(doAutoRedirect(currentTicketId, nextTicketId))
        }
      })
    }

    return graphql(token, mutation, variables)
      .then(res => {
        const response = res.json.data
        const actions = []
        const { tickets } = response.createBulkChangeset
        const ticketsWithOptimisticSearchDiff = tickets.map((ticket, index) => {
          const optimisticTicketData = optimisticData[index]
          const optimisticSearches =
            optimisticTicketData &&
            optimisticTicketData.diff &&
            optimisticTicketData.diff.searches
          const ticketDiff = JSON.parse(ticket.diff)
          const ticketSearches =
            ticketDiff && normalizeDiffSearchesPayload(ticketDiff.searches)

          return {
            ...ticket,
            diff: {
              ...ticketDiff,
              searches: {
                ...optimisticSearches,
                ...ticketSearches,
              },
            },
          }
        })
        actions.push({
          type: types.CREATE_BULK_CHANGESET_SUCCESS,
          data: {
            tickets: ticketsWithOptimisticSearchDiff,
            snackbar,
          },
          // We REVERT not COMMIT otherwise the filter diff counts may be wrong.
          optimist: optimisticData ? { type: REVERT, id: changesetId } : null,
        })

        const responseData = response.createBulkChangeset

        responseData.tickets.forEach(ticket => {
          // eslint-disable-next-line no-param-reassign
          ticket.diff = JSON.parse(ticket.diff) || {}
          actions.push(
            doAddChangeset(
              ticket.id,
              ticket.ticket,
              ticket.changesetId,
              ticket.actions.records,
              ticket.diff
            )
          )
        })

        actions.push(
          doClearNonNamedTicketSearchResults({
            keepCurrent: true,
            refetchListOnNextUpdate: true,
          })
        )
        dispatch(batchActions(actions))
      })
      .catch(err => {
        debug(err)
        dispatch({
          type: types.CREATE_BULK_CHANGESET_FAIL,
          optimist: optimisticData ? { type: REVERT, id: changesetId } : null,
        })
      })
      .then(() => dispatch({ type: types.CREATE_BULK_CHANGESET_COMPLETE }))
  }
}

function doAddChangeset(
  ticketId,
  ticketData,
  changesetId,
  actions,
  diff,
  preserveDraft = false
) {
  return {
    type: types.ADD_CHANGESET,
    data: {
      ticketId,
      changesetId,
      actions,
      ticketData,
      diff,
      preserveDraft,
    },
  }
}
